package Structs

import DataBase.UINKDBInterface
import Utils.getJsonWithConfig
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonArray
import myName

/**
 * A destination bank account for deposits (הפקדות), stored as one entry of the
 * `bank_accounts` JSON array on the hashavshevet conf row rather than in its own
 * table. Editable from the deposits screen (and the admin panel).
 *
 * [hash_account] is the Hashavshevet account code written as חשבון חובה on the
 * deposit movement line; [id] is referenced by `Deposit.bank_account_id`.
 */
@myName("BankAccountConf")
@Serializable
data class BankAccountConf(
    val id: Int = 0,
    val name: String = "",
    val bank_id: String = "",
    val branch: String = "",
    val account: String = "",
    val hash_account: String = "",
    val default: Int = 0,
) {
    @myName("getDisplayName")
    fun getDisplayName(): String {
        if (name.isNotEmpty()) return name
        val parts = listOf(bank_id, branch, account).filter { it.isNotEmpty() }
        return if (parts.isEmpty()) "" else parts.joinToString("-")
    }

    companion object {
        /**
         * Tolerant on purpose: the column is blank for every company that has not
         * configured deposits yet, and must never break config loading.
         */
        fun createFromJson(js: String?): List<BankAccountConf> {
            if (js.isNullOrBlank()) return listOf()
            return try {
                val arr = getJsonWithConfig().parseToJsonElement(js.replace("\\'", "'")) as JsonArray
                arr.map { getJsonWithConfig().decodeFromJsonElement(serializer(), it) }
            } catch (e: Exception) {
                listOf()
            }
        }
    }
}

@Serializable
data class HashavshevetS(
    val movein: Int,
    val imovein: Int,
    val maam: String,
    val income_with: String,
    val income_without: String,
    val cash_payment: String,
    val bank_payment: String,
    val check_payment: String,
    val other_payments: String,
    val mass_bamacor:String,
    val other_clients: Int,
    val clientsIndex:Int,
    val productsIndex:Int,
    val tax_move:String,
    val tax_cancel_move:String,
    val pay_move:String,
    val hand_sum_with:String,
    val hand_sum_without:String,
    val note_sum_with:String,
    val note_sum_without:String,
    val taxnote_sum_with:String,
    val taxnote_sum_without:String,
    val rivhit:Int = 0, // 1 if rivhit ,2,3    10- bkmvdata
    val miun:Int=0,
    val last_invoice_id: Int = 0,
    val last_refund_id: Int = 0,
    val last_invoice_payment_id: Int = 0,
    val last_receipt_id: Int = 0,
    /** High-water mark for הפקדות exported via checkpoint-mode Hashavshevet. */
    val last_deposit_id: Int = 0,
    /** סוג תנועה for deposit movements. Blank disables the deposit export. */
    val deposit_move: String = "",
    /** JSON array of [BankAccountConf]. Blank disables the deposit export. */
    val bank_accounts: String = "",
    /** Purchase / expense account (with VAT). Blank together with [expense_without] disables AP MOVEIN. */
    val expense_with: String = "",
    /** Purchase / expense account (exempt / no VAT). */
    val expense_without: String = "",
    /** Input VAT (מע"מ תשומות). Required when a supplier invoice has VAT. */
    val maam_in: String = "",
    /** סוג תנועה for supplier invoices. Blank falls back to [tax_move]. */
    val sup_tax_move: String = "",
    /** סוג תנועה for supplier credit notes. Blank falls back to [tax_cancel_move]. */
    val sup_tax_cancel_move: String = "",
    /** סוג תנועה for supplier payments. Blank falls back to [pay_move]. */
    val sup_pay_move: String = "",

) {
    val to_rivhit = rivhit ==1

    // Parsed once, then read through a computed getter: the shimmed `bankAccounts`
    // name must not also be a backing field the constructor writes, or every
    // HashavshevetS built in the browser throws. See [DepositLine.payMethod].
    private val parsedBankAccounts: List<BankAccountConf> =
        BankAccountConf.createFromJson(bank_accounts)

    val bankAccounts: List<BankAccountConf> get() = parsedBankAccounts

    @myName("bankAccount")
    fun bankAccount(id: Int): BankAccountConf? = bankAccounts.firstOrNull { it.id == id }

    /** True once a movement type and at least one destination account are configured. */
    @myName("depositsConfigured")
    fun depositsConfigured(): Boolean = deposit_move.isNotEmpty() && bankAccounts.isNotEmpty()

    /**
     * True once at least one AP expense account is set. Until then supplier
     * invoices/payments are not written to MOVEIN, so existing companies stay inert.
     */
    @myName("supplierApConfigured")
    fun supplierApConfigured(): Boolean =
        expense_with.isNotEmpty() || expense_without.isNotEmpty()
}
